import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Gift, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { SPIN_PRIZES } from '../utils/pricing';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SpinWheel = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSpinning, setIsSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [result, setResult] = useState(null);
  const [showResult, setShowResult] = useState(false);

  const getFingerprint = () => {
    const fp = localStorage.getItem('joywin_fp') || `fp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('joywin_fp', fp);
    return fp;
  };

  useEffect(() => {
    // Auto-close: don't show spin wheel after May 15th, 2026
    const cutoff = new Date('2026-05-15T23:59:59');
    if (new Date() > cutoff) return;
    // Show popup on homepage load after a short delay
    const timer = setTimeout(() => setIsOpen(true), 1500);
    return () => clearTimeout(timer);
  }, []);

  const spin = async () => {
    if (isSpinning) return;

    setIsSpinning(true);
    setShowResult(false);
    const fingerprint = getFingerprint();

    try {
      const response = await axios.post(`${API}/spin/result`, { fingerprint });
      const { discount, discount_code } = response.data;

      // Find the index of the winning segment
      const winningIndex = SPIN_PRIZES.findIndex(p => p.value === discount);
      
      // Calculate rotation to land pointer on center of winning segment
      const segmentAngle = 360 / SPIN_PRIZES.length;
      const targetOffset = winningIndex * segmentAngle + segmentAngle / 2;
      const targetModulo = ((360 - targetOffset) % 360 + 360) % 360;
      const currentModulo = ((rotation % 360) + 360) % 360;
      const delta = ((targetModulo - currentModulo) % 360 + 360) % 360;
      const fullSpins = 5 * 360;
      const newRotation = rotation + fullSpins + delta;

      setRotation(newRotation);

      // Wait for animation to complete
      setTimeout(() => {
        setResult({
          discount,
          discountCode: discount_code
        });
        setShowResult(true);
        setIsSpinning(false);
        
        // Save to localStorage
        if (discount) {
          localStorage.setItem('joywin_discount', discount.toString());
        }
        if (discount_code) {
          localStorage.setItem('joywin_discountCode', discount_code);
        }
      }, 5000);
    } catch (error) {
      console.error('Error spinning:', error);
      setIsSpinning(false);
    }
  };

  const closeModal = () => {
    setIsOpen(false);
    setShowResult(false);
  };

  const spinAgain = () => {
    setShowResult(false);
    setResult(null);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
        onClick={(e) => e.target === e.currentTarget && !isSpinning && closeModal()}
        data-testid="spin-wheel-modal"
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
          className="relative bg-[#0a0a0a] border border-[#D4AF37]/30 rounded-2xl p-8 max-w-lg w-full"
        >
          {/* Close button */}
          {!isSpinning && (
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 text-[#A1A1AA] hover:text-white transition-colors"
              data-testid="spin-close-btn"
            >
              <X className="w-6 h-6" />
            </button>
          )}

          {/* Header */}
          <div className="text-center mb-6">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Sparkles className="w-6 h-6 text-[#D4AF37]" />
              <h2 className="text-2xl font-['Playfair_Display'] gold-gradient-text">
                Limited Time Offer!
              </h2>
              <Sparkles className="w-6 h-6 text-[#D4AF37]" />
            </div>
            <p className="text-[#A1A1AA] text-sm">
              We heard you loved it last time, so we're back!
            </p>
            <p className="text-[#EDEDED] font-semibold mt-1">
              Spin to win up to 30% OFF on our packages!
            </p>
          </div>

          {!showResult ? (
            <>
              {/* Wheel */}
              <div className="relative w-72 h-72 mx-auto mb-6">
                {/* Pointer */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-2 z-10">
                  <div className="w-0 h-0 border-l-[12px] border-r-[12px] border-t-[20px] border-l-transparent border-r-transparent border-t-[#D4AF37]"></div>
                </div>

                {/* Wheel */}
                <motion.div
                  className="w-full h-full rounded-full border-4 border-[#D4AF37] overflow-hidden"
                  style={{ rotate: rotation }}
                  animate={{ rotate: rotation }}
                  transition={{ duration: 5, ease: [0.2, 0.8, 0.2, 1] }}
                >
                  <svg viewBox="0 0 100 100" className="w-full h-full">
                    {SPIN_PRIZES.map((prize, index) => {
                      const angle = 360 / SPIN_PRIZES.length;
                      const startAngle = index * angle - 90;
                      const endAngle = startAngle + angle;
                      const startRad = (startAngle * Math.PI) / 180;
                      const endRad = (endAngle * Math.PI) / 180;
                      const x1 = 50 + 50 * Math.cos(startRad);
                      const y1 = 50 + 50 * Math.sin(startRad);
                      const x2 = 50 + 50 * Math.cos(endRad);
                      const y2 = 50 + 50 * Math.sin(endRad);
                      const largeArc = angle > 180 ? 1 : 0;
                      const midAngle = startAngle + angle / 2;
                      const midRad = (midAngle * Math.PI) / 180;
                      const textX = 50 + 32 * Math.cos(midRad);
                      const textY = 50 + 32 * Math.sin(midRad);

                      return (
                        <g key={index}>
                          <path
                            d={`M 50 50 L ${x1} ${y1} A 50 50 0 ${largeArc} 1 ${x2} ${y2} Z`}
                            fill={prize.color}
                            stroke="#D4AF37"
                            strokeWidth="0.5"
                          />
                          <text
                            x={textX}
                            y={textY}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            fill={prize.color === '#D4AF37' ? '#000' : '#D4AF37'}
                            fontSize="6"
                            fontWeight="bold"
                            transform={`rotate(${midAngle + 90}, ${textX}, ${textY})`}
                          >
                            {prize.label}
                          </text>
                        </g>
                      );
                    })}
                    <circle cx="50" cy="50" r="8" fill="#0a0a0a" stroke="#D4AF37" strokeWidth="1" />
                  </svg>
                </motion.div>
              </div>

              {/* Spin Button */}
              <Button
                onClick={spin}
                disabled={isSpinning}
                className="w-full gold-gradient-btn py-4 text-lg rounded-full disabled:opacity-50"
                data-testid="spin-btn"
              >
                {isSpinning ? 'Spinning...' : 'SPIN TO WIN!'}
              </Button>
            </>
          ) : (
            /* Result Display */
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="text-center py-8"
              data-testid="spin-result"
            >
              <Gift className="w-16 h-16 text-[#D4AF37] mx-auto mb-4" />
              {result?.discount > 0 ? (
                <>
                  <h3 className="text-3xl font-['Playfair_Display'] gold-gradient-text mb-2">
                    Your Discount!
                  </h3>
                  <p className="text-xl text-[#EDEDED] mb-4">
                    You have <span className="text-[#D4AF37] font-bold">{result.discount}% OFF</span>!
                  </p>
                  <div className="bg-[#1a1a1a] rounded-lg p-4 mb-4">
                    <p className="text-[#A1A1AA] text-sm mb-1">Your discount code:</p>
                    <p className="text-[#D4AF37] font-mono text-lg font-bold" data-testid="discount-code">
                      {result.discountCode}
                    </p>
                  </div>
                  <p className="text-[#A1A1AA] text-sm">
                    Use this code when booking to claim your discount!
                  </p>
                  <p className="text-[#52525B] text-xs mt-2">
                    *Applies to orders over £200. Resets after booking!
                  </p>
                </>
              ) : (
                <>
                  <h3 className="text-2xl font-['Playfair_Display'] text-[#EDEDED] mb-2">
                    No Discount This Time
                  </h3>
                  <p className="text-[#A1A1AA] mb-4">
                    But our packages are still amazing value!
                  </p>
                </>
              )}
              <div className="flex gap-3 mt-6 justify-center">
                <Button
                  onClick={spinAgain}
                  variant="outline"
                  className="border-[#D4AF37] text-[#D4AF37] rounded-full"
                  data-testid="spin-again-btn"
                >
                  Spin Again
                </Button>
                <Button
                  onClick={closeModal}
                  className="gold-gradient-btn rounded-full"
                  data-testid="spin-continue-btn"
                >
                  Continue to Website
                </Button>
              </div>
            </motion.div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SpinWheel;
